/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import { useResizeObserver } from '@react-aria/utils';
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, type ReactElement, createContext, forwardRef, useCallback, useRef, useState } from 'react';
import { ComboBox as ComboBoxCore, type ComboBoxProps as ComboBoxCoreProps, type ContextValue, Provider, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { HintContext } from '../hint/hint.component';
import { IconButton } from '../icon-button/icon-button.component';
import { INPUT_SLOTS, InputStyleContext } from '../input/input.component';
import { LabelContext, type LabelProps } from '../label/label.component';
import { LabeledInputStyleContext } from '../labeled-input/labeled-input.component';
import { ListBoxItemContext } from '../list-box-item/list-box-item.component';
import { ListBoxContext } from '../list-box/list-box.component';
import { PopoverContext } from '../popover/popover.component';

interface ComboBoxStyleProps {
  /** The size variant of the text field. (Default: 48) */
  size?: keyof typeof sizeVariants;
  style?: stylex.StyleXStyles;
}

type ComboBoxProps<T extends object> = Omit<ComboBoxCoreProps<T>, 'style' | 'className'> & ComboBoxStyleProps;

const ComboBoxStyleContext = createContext<ContextValue<ComboBoxStyleProps, HTMLDivElement>>({});

const ComboBox = forwardRef((props: ComboBoxProps<object>, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, ComboBoxStyleContext);
  const inputRef = useRef<HTMLDivElement>(null);

  const { style, children, size = 48, menuTrigger = 'focus', ...restProps } = props;

  const [menuWidth, setMenuWidth] = useState('0px');
  const onResize = useCallback(() => {
    if (inputRef.current) {
      const inputRect = inputRef.current.getBoundingClientRect();
      setMenuWidth(inputRect.width + 'px');
    }
  }, [inputRef]);

  useResizeObserver({
    ref: inputRef,
    onResize: onResize
  });

  return (
    <ComboBoxCore
      ref={ref}
      menuTrigger={menuTrigger}
      {...stylex.props(styles.base, sizeVariants[size], style)}
      {...restProps}>
      {(renderProps) => (
        <Provider values={[
          [LabelContext, { size: LABEL_SIZES[size] }],
          [InputStyleContext, {
            size,
            ref: inputRef,
            children: (inputRenderProps) => (
              <>
                {inputRenderProps.defaultChildren}
                <IconButton slot={INPUT_SLOTS.POPOVER_ICON_BUTTON} />
              </>
            )
          }],
          [LabeledInputStyleContext, {
            ref: inputRef,
            children: (inputRenderProps) => (
              <>
                {inputRenderProps.defaultChildren}
                <IconButton slot={INPUT_SLOTS.POPOVER_ICON_BUTTON} />
              </>
            )
          }],
          [ListBoxItemContext, { style: listBoxItemStyles.base }],
          [HintContext, { style: hintSizeVariants[size] }],
          [ListBoxContext, { style: listBoxStyles.base }],
          [PopoverContext, { triggerRef: inputRef, style: popoverStyles.base(menuWidth) }]
        ]}>
          {typeof children === 'function' ? children?.(renderProps) : children}
        </Provider>
      )}
    </ComboBoxCore>
  );
}) as <T extends object>(p: ComboBoxProps<T> & { ref?: ForwardedRef<HTMLDivElement> }) => ReactElement;

const LABEL_SIZES: Record<keyof typeof sizeVariants, LabelProps['size']> = {
  48: 16,
  32: 13,
};

const styles = stylex.create({
  base: {
    display: 'flex',
    flexDirection: 'column'
  },
});

const listBoxStyles = stylex.create({
  base: {
    borderRadius: 10,
    paddingBlock: 4,
    backgroundColor: $.surfaceBGPrimary,
    boxShadow: $.shadowCenter070Loud,
  }
});

const listBoxItemStyles = stylex.create({
  base: {
    /**
     *  This spacing should normally belong to the ListBox, but if the ListBox is used as a virtualized list, 
     *  the Virtualizer displays an inner wrapper with a width = visibleRect.width. 
     *  This 4px indentation on both sides in the ListBox will shrink the content box, so the virtualizer's inner wrapper will ultimately be 8 pixels wider than the available space.
     *  To avoid this issue, we add this spacing to ListBoxItem while keeping the top and bottom spacing in ListBox.
     */
    marginInline: 4,
  }
});

const popoverStyles = stylex.create({
  base: (width: string) => ({
    width: width,
  })
});

const sizeVariants = stylex.create({
  48: { gap: 6 },
  32: { gap: 4 }
});

const hintSizeVariants = stylex.create({
  48: { marginTop: -2 },
  32: {}
});

export { ComboBox, ComboBoxStyleContext, type ComboBoxProps, type ComboBoxStyleProps };

